import {useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import styled from 'styled-components';
import aiIconUrl from '../../../assets/icons/ai.svg';
import {relativeTime} from '../utils/relativeTime';
import {useAiSummaryCollapse} from '../hooks/useAiSummaryCollapse';
import {useRegenerateAiSummary} from '../hooks/useRegenerateAiSummary';
import {useSubmitAiSummaryFeedback} from '../hooks/useSubmitAiSummaryFeedback';
import type {AiSummaryBlock_ticket$key} from './__generated__/AiSummaryBlock_ticket.graphql';

const AiSummaryFragment = graphql`
	fragment AiSummaryBlock_ticket on Ticket {
		id
		aiSummaryState
		aiSummary {
			summary
			statusSignal
			sentiment
			commentCount
			emailCount
			generatedAt
			modelVersion
			feedbackScore
			feedbackText
			isFinal
			staleness {
				isStale
				newComments
				newEmails
				newStatusEvents
				total
			}
		}
	}
`;

const Section = styled.section`
	padding: 20px;
	background: ${p => p.theme.ticketing.color.brand50};
	border: 1px solid ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.m};
`;
const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
`;
const HeaderButton = styled.button`
	display: flex;
	align-items: center;
	padding: 0;
	border: 0;
	background: transparent;
	cursor: pointer;
`;
const Left = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const Right = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const Icon = styled.img`
	width: 16px;
	height: 16px;
	color: ${p => p.theme.ticketing.color.interactivePrimary};
`;
const Title = styled.span`
	font: ${p => p.theme.ticketing.font.label1};
	color: ${p => p.theme.ticketing.color.interactivePrimary};
`;
const AiPill = styled.span`
	padding: 2px 8px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	background: ${p => p.theme.ticketing.color.brand100};
	color: ${p => p.theme.ticketing.color.interactivePrimary};
	border-radius: ${p => p.theme.ticketing.radius.full};
`;
const Timestamp = styled.span`
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.textSecondary};
`;
const Body = styled.p`
	margin: 12px 0 0;
	font: ${p => p.theme.ticketing.font.body2};
	color: ${p => p.theme.ticketing.color.dark900};
	white-space: pre-wrap;
`;
const Provenance = styled.div`
	margin-top: 8px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.textSecondary};
`;
const LoadingRow = styled.div`
	margin-top: 12px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.textSecondary};
`;
const StalenessPill = styled.span`
	padding: 2px 8px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: ${p => p.theme.ticketing.color.interactivePrimary};
	background: ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.full};
	cursor: default;
`;
const RegenerateButton = styled.button`
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.interactivePrimary};
	background: transparent;
	border: 1px solid ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.m};
	padding: 4px 10px;
	cursor: pointer;
	&:disabled {
		opacity: 0.6;
		cursor: default;
	}
`;
const ErrorRow = styled.div`
	margin-top: 8px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.dangerText};
`;
const ThumbButton = styled.button<{$selected: boolean}>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => (p.$selected ? p.theme.ticketing.color.interactivePrimary : p.theme.ticketing.color.textSecondary)};
	background: ${p => (p.$selected ? p.theme.ticketing.color.brand100 : 'transparent')};
	border: 1px solid ${p => (p.$selected ? p.theme.ticketing.color.interactivePrimary : p.theme.ticketing.color.brand100)};
	border-radius: ${p => p.theme.ticketing.radius.m};
	cursor: pointer;
	&:disabled {
		opacity: 0.6;
		cursor: default;
	}
`;
const FeedbackNote = styled.div`
	margin-top: 8px;
	display: flex;
	flex-direction: column;
	gap: 6px;
`;
const NoteInput = styled.textarea`
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.dark900};
	border: 1px solid ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.m};
	padding: 6px 8px;
	resize: vertical;
	min-height: 48px;
`;
const NoteActions = styled.div`
	display: flex;
	gap: 8px;
`;
const Placeholder = styled.div`
	margin-top: 12px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.textSecondary};
`;
const FinalLabel = styled.span`
	padding: 2px 8px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: ${p => p.theme.ticketing.color.interactivePrimary};
	background: ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.full};
`;
const RetryButton = styled.button`
	margin-top: 8px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.interactivePrimary};
	background: transparent;
	border: 1px solid ${p => p.theme.ticketing.color.brand100};
	border-radius: ${p => p.theme.ticketing.radius.m};
	padding: 4px 10px;
	cursor: pointer;
	&:disabled {
		opacity: 0.6;
		cursor: default;
	}
`;

function pluralise(count: number, noun: string): string {
	return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

interface Props {
	ticketRef: AiSummaryBlock_ticket$key;
	/** True while a generation job is in flight and no summary exists yet (AC #8). */
	generating?: boolean;
}

export function AiSummaryBlock({ticketRef, generating = false}: Props) {
	const ticket = useFragment<AiSummaryBlock_ticket$key>(AiSummaryFragment, ticketRef);
	const {collapsed, toggle} = useAiSummaryCollapse(ticket.id);
	const {regenerate, inFlight, error} = useRegenerateAiSummary();
	const {submit: submitFeedback, inFlight: feedbackInFlight} = useSubmitAiSummaryFeedback();
	const summary = ticket.aiSummary;
	const state = ticket.aiSummaryState;
	const isFinal = Boolean(summary?.isFinal);
	const busy = generating || inFlight;

	// AC #2/#5: the current per-summary vote. Re-clicking the selected thumb clears
	// it to null; the "what was wrong?" note appears only while a downvote is active.
	const score = summary?.feedbackScore ?? null;
	const [showNote, setShowNote] = useState(false);
	const [note, setNote] = useState(summary?.feedbackText ?? '');

	const vote = (next: 1 | -1) => {
		if (score === next) {
			setShowNote(false);
			submitFeedback({ticketId: ticket.id, score: null, feedbackText: null});
			return;
		}
		if (next === -1) {
			setShowNote(true);
			submitFeedback({ticketId: ticket.id, score: -1, feedbackText: summary?.feedbackText ?? null});
		} else {
			setShowNote(false);
			submitFeedback({ticketId: ticket.id, score: 1, feedbackText: null});
		}
	};

	// AC #1: no thread yet — placeholder, never a fabricated summary.
	if (!summary && state === 'NO_ACTIVITY') {
		return (
			<Section>
				<Left>
					<Icon src={aiIconUrl} alt="" role="img" />
					<Title>AI summary</Title>
					<AiPill>AI</AiPill>
				</Left>
				<Placeholder>AI summary will appear once the ticket has activity.</Placeholder>
			</Section>
		);
	}

	// AC #2/#5: the latest generation failed — quiet error with a retry.
	if (!summary && state === 'FAILED') {
		return (
			<Section>
				<Left>
					<Icon src={aiIconUrl} alt="" role="img" />
					<Title>AI summary</Title>
					<AiPill>AI</AiPill>
				</Left>
				<Placeholder>The AI summary couldn’t be generated. You can try again.</Placeholder>
				<RetryButton type="button" disabled={busy} onClick={() => regenerate(ticket.id)}>
					{inFlight ? 'Trying…' : 'Try again'}
				</RetryButton>
				{error && <ErrorRow>{error.message}</ErrorRow>}
			</Section>
		);
	}

	// AC #3/#8: loading affordance — an explicit generating prop, an in-flight
	// regenerate, or the gateway reporting a pending (activity but no summary yet)
	// state. Covers the first generation and a regenerate of an empty block.
	if (!summary && (busy || state === 'PENDING')) {
		return (
			<Section>
				<Left>
					<Icon src={aiIconUrl} alt="" role="img" />
					<Title>AI summary</Title>
					<AiPill>AI</AiPill>
				</Left>
				<LoadingRow>{inFlight ? 'Regenerating…' : 'Generating summary…'}</LoadingRow>
			</Section>
		);
	}

	// READY but no row (defensive) — render nothing.
	if (!summary) return null;

	const provenance = `${pluralise(summary.commentCount, 'comment')} · ${pluralise(summary.emailCount, 'email')}`;
	const staleness = summary.staleness;
	const stalenessTitle = staleness
		? `${pluralise(staleness.newComments, 'comment')}, ${pluralise(staleness.newEmails, 'email')}, ${pluralise(
				staleness.newStatusEvents,
				'status change'
		  )}`
		: undefined;

	return (
		<Section>
			<Header>
				<HeaderButton type="button" onClick={toggle} aria-expanded={!collapsed} aria-label="AI summary">
					<Left>
						<Icon src={aiIconUrl} alt="" role="img" />
						<Title>AI summary</Title>
						<AiPill>AI</AiPill>
					</Left>
				</HeaderButton>
				<Right>
					{!isFinal && staleness?.isStale && staleness.total > 0 && (
						<StalenessPill title={stalenessTitle}>
							{pluralise(staleness.total, 'new item')} since last summary — regenerate?
						</StalenessPill>
					)}
					{isFinal && <FinalLabel>Final summary</FinalLabel>}
					<Timestamp>{relativeTime(summary.generatedAt)}</Timestamp>
					<ThumbButton
						type="button"
						aria-label="Thumbs up"
						aria-pressed={score === 1}
						$selected={score === 1}
						disabled={feedbackInFlight}
						onClick={() => vote(1)}
					>
						👍
					</ThumbButton>
					<ThumbButton
						type="button"
						aria-label="Thumbs down"
						aria-pressed={score === -1}
						$selected={score === -1}
						disabled={feedbackInFlight}
						onClick={() => vote(-1)}
					>
						👎
					</ThumbButton>
					{!isFinal && (
						<RegenerateButton type="button" disabled={busy} onClick={() => regenerate(ticket.id)}>
							{inFlight ? 'Regenerating…' : 'Regenerate'}
						</RegenerateButton>
					)}
				</Right>
			</Header>
			{!collapsed && (
				<>
					<Body>{summary.summary}</Body>
					<Provenance>{provenance}</Provenance>
				</>
			)}
			{showNote && !collapsed && (
				<FeedbackNote>
					<NoteInput
						value={note}
						placeholder="What was wrong? (optional)"
						onChange={e => setNote(e.target.value)}
						aria-label="What was wrong?"
					/>
					<NoteActions>
						<RegenerateButton
							type="button"
							disabled={feedbackInFlight}
							onClick={() => {
								submitFeedback({ticketId: ticket.id, score: -1, feedbackText: note.trim() || null});
								setShowNote(false);
							}}
						>
							Save note
						</RegenerateButton>
					</NoteActions>
				</FeedbackNote>
			)}
			{error && <ErrorRow>{error.message}</ErrorRow>}
		</Section>
	);
}
